import classNames from "classnames";
import { ReactComponent as IconAlertCircle } from "kustom-front/src/icons/alert-circle.svg";
import { ReactComponent as IconAlertTriangle } from "kustom-front/src/icons/alert-triangle.svg";
import { ReactComponent as IconCheck } from "kustom-front/src/icons/check.svg";
import { ReactComponent as IconX } from "kustom-front/src/icons/x.svg";
import join from "lodash/join";
import React, { FunctionComponent, useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import useNotificationsContext from "@Hooks/useNotificationsContext";
import { Notification as NotificationType } from "@Types/notifications";
import { getNotificationData } from "@Utils/notifications";
import "./style.scss";
import { calcAutoCloseDelay } from "./utils";

interface Props {
  notification: NotificationType;
}

const Notification: FunctionComponent<Props> = ({ notification }) => {
  const { dispatchNotification } = useNotificationsContext();

  const { code, message, key } = notification;
  const { autoClose, type, undoable } = getNotificationData(code);

  const close = useCallback(
    () => dispatchNotification({ notificationKey: key, type: "removeNotification" }),
    [dispatchNotification, key]
  );

  const autoCloseDelay = calcAutoCloseDelay(autoClose, undoable);

  useEffect(() => {
    if (autoCloseDelay <= 0) {
      return;
    }

    const timeoutID = setTimeout(() => {
      dispatchNotification({ notificationKey: key, type: "removeNotification" });
    }, autoCloseDelay);

    return () => clearTimeout(timeoutID);
  }, [autoCloseDelay, close, dispatchNotification, key]);

  const codes = [code];

  let Icon;
  switch (type) {
    case "error":
      Icon = IconAlertCircle;
      break;
    case "warning":
      Icon = IconAlertTriangle;
      break;
    case "validation":
      Icon = IconCheck;
      break;
    default:
      const exhaustiveCheck: never = type;
      throw new Error(
        `Unsupported notification type ${(exhaustiveCheck as { type: string }).type}`
      );
  }

  return (
    <div className={classNames("Notification", `Notification--${type}`)}>
      <div className="Notification-icon" title={join(codes, " ")}>
        <Icon />
      </div>

      <div className="Notification-message">
        {message ? <span>{message}</span> : <FormattedMessage id={`cheerz_Notification_${code}`} />}
      </div>

      <div className="Notification-close" onClick={close}>
        <IconX />
      </div>
    </div>
  );
};

export default Notification;
